import { CalendarDays, CheckCircle2, Clock3, BarChart3, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtDate } from '@/features/shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { IzinTalepModal } from '../components/actions/IzinTalepModal'
import { KpiCard } from '../components/KpiCard'
import { MobileList } from '../components/MobileList'
import { TABLE_FRAME } from '../components/tableFrame'
import { SectionCard } from '../components/SectionCard'
import type { PersonelDetail } from '../model'

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
import { HR_WRITE_PERM } from '../../permissions'

function izinTone(d: 'onaylandi' | 'bekliyor' | 'red'): BadgeTone {
  if (d === 'onaylandi') return 'success'
  if (d === 'bekliyor') return 'warning'
  return 'danger'
}

function monthGrid(now: Date, izinGunleri: string[]) {
  const y = now.getFullYear()
  const m = now.getMonth()
  const prefix = `${y}-${String(m + 1).padStart(2, '0')}-`
  const count = new Date(y, m + 1, 0).getDate()
  const days = Array.from({ length: count }, (_, i) => i + 1)
  const weekend = new Set(days.filter((d) => [0, 6].includes(new Date(y, m, d).getDay())))
  const leaveDays = new Set(izinGunleri.filter((d) => d.startsWith(prefix)).map((d) => Number(d.slice(8))))
  return {
    label: `${AYLAR[m]} ${y}`,
    days,
    blanks: Array.from({ length: (new Date(y, m, 1).getDay() + 6) % 7 }, (_, i) => i),
    weekend,
    leaveDays,
    today: now.getDate(),
  }
}

export function IzinMesaiTab({ personelId, izin }: { personelId: string; izin: PersonelDetail['izin'] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [sub, setSub] = useState<'puantaj' | 'talepler'>('puantaj')
  const [talepOpen, setTalepOpen] = useState(false)
  const [now] = useState(() => new Date())
  const { label: ayLabel, days, blanks, weekend, leaveDays, today } = monthGrid(now, izin.izinGunleri)
  const rows = sub === 'puantaj' ? izin.puantaj : izin.talepler.map((r) => ({
    tarih: r.baslangic,
    tur: r.tur,
    sure: r.gun,
    durum: r.durum,
  }))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard label={t('hr.personel.izin.yillik')} value={`${izin.kpi.yillik_hak} ${t('hr.personel.izin.gun')}`} tint="info" icon={<CalendarDays className="size-5" />} />
        <KpiCard label={t('hr.personel.izin.kullanilan')} value={`${izin.kpi.kullanilan} ${t('hr.personel.izin.gun')}`} tint="success" icon={<CheckCircle2 className="size-5" />} />
        <KpiCard label={t('hr.personel.izin.kalan')} value={`${izin.kpi.kalan} ${t('hr.personel.izin.gun')}`} tint="primary" icon={<Clock3 className="size-5" />} />
        <KpiCard label={t('hr.personel.izin.mesai')} value={`${izin.kpi.mesai} ${t('hr.personel.izin.saat')}`} hint={ayLabel} tint="warning" icon={<BarChart3 className="size-5" />} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
        <SectionCard title={t('hr.personel.izin.takvim')} description={ayLabel}>
          <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase text-muted-foreground">
            {['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {blanks.map((b) => (
              <div key={`b-${b}`} className="h-9" />
            ))}
            {days.map((d) => (
              <div
                key={d}
                className={cn(
                  'relative flex h-9 items-center justify-center rounded-md text-xs tabular-nums',
                  weekend.has(d) && 'text-destructive/70',
                  leaveDays.has(d) && 'bg-info/15 font-medium text-info',
                  d === today && 'bg-primary text-primary-foreground font-semibold',
                )}
              >
                {d}
                {!leaveDays.has(d) && !weekend.has(d) && d < today ? (
                  <span className="absolute bottom-1 size-1 rounded-full bg-success" />
                ) : null}
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-success" />
              {t('hr.personel.izin.legend.calisti')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-sm bg-info/40" />
              {t('hr.personel.izin.legend.izin')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-destructive/50" />
              {t('hr.personel.izin.legend.haftaSonu')}
            </span>
          </div>
        </SectionCard>

        <SectionCard
          title={
            <Tabs value={sub} onValueChange={(v) => setSub(v as 'puantaj' | 'talepler')}>
              <TabsList size="sm">
                <TabsTrigger value="puantaj">{t('hr.personel.izin.puantaj')}</TabsTrigger>
                <TabsTrigger value="talepler">{t('hr.personel.izin.talepler')}</TabsTrigger>
              </TabsList>
            </Tabs>
          }
          action={
            can(HR_WRITE_PERM) ? (
              <Button size="sm" onClick={() => setTalepOpen(true)}>
                <Plus />
                {t('hr.personel.izin.yeniTalep')}
              </Button>
            ) : undefined
          }
          noPad
        >
          <MobileList
            rows={rows.map((r, i) => ({
              key: `${r.tarih}-${r.tur}-${i}`,
              title: r.tur,
              subtitle: fmtDate(r.tarih),
              trailing: <Badge tone={izinTone(r.durum)}>{t(`hr.personel.izin.durum.${r.durum}`)}</Badge>,
              meta: (
                <span>
                  {t('hr.personel.izin.sure')}: <span className="tabular-nums text-foreground">{r.sure}</span>
                </span>
              ),
            }))}
          />
          <Table className="hr-table" wrapperClassName={TABLE_FRAME}>
            <THead>
              <TR>
                <TH>{t('hr.personel.izin.baslangic')}</TH>
                <TH>{t('hr.personel.izin.tur')}</TH>
                <TH>{t('hr.personel.izin.sure')}</TH>
                <TH>{t('hr.personel.columns.durum')}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((r, i) => (
                <TR key={`${r.tarih}-${r.tur}-${i}`}>
                  <TD>{fmtDate(r.tarih)}</TD>
                  <TD className="font-medium">{r.tur}</TD>
                  <TD className="tabular-nums">{r.sure}</TD>
                  <TD>
                    <Badge tone={izinTone(r.durum)}>{t(`hr.personel.izin.durum.${r.durum}`)}</Badge>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </SectionCard>
      </div>

      {talepOpen ? (
        <IzinTalepModal personelId={personelId} onClose={() => setTalepOpen(false)} onCreated={() => setSub('talepler')} />
      ) : null}

      <SectionCard title={t('hr.personel.izin.onayTimeline')}>
        <div className="space-y-0">
          {izin.onay.map((s, i) => (
            <div key={s.title} className="relative flex gap-3 pb-5 last:pb-0">
              {i < izin.onay.length - 1 ? (
                <div className="absolute top-4 left-[9px] h-[calc(100%-8px)] w-px bg-border" />
              ) : null}
              <div
                className={cn(
                  'relative z-10 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2',
                  s.state === 'done' && 'border-success bg-success text-success-foreground',
                  s.state === 'act' && 'border-primary bg-card',
                )}
              >
                {s.state === 'done' ? <CheckCircle2 className="size-3.5 text-white" /> : null}
              </div>
              <div>
                <div className="text-sm font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground">{s.at}</div>
                <div className="mt-0.5 text-sm text-muted-foreground">{s.by}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  )
}
